import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  PiArrowsDownUp,
  PiCaretLeft,
  PiCaretRight,
  PiCopy,
  PiDotsThree,
  PiDownloadSimple,
  PiFunnel,
  PiPencilSimple,
  PiPlus,
  PiTrash,
} from 'react-icons/pi'
import { secondaryPillButtonClass } from 'components/FbUI/secondaryPillButton'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { cn } from 'components/UI/cn'
import {
  addReportToDashboard,
  createDashboardFromReport,
  listSavedDashboards,
} from './saved-dashboards'
import { resolveThreshold, useKpis } from './kpis'
import { REPORTING_PAGE_SHELL } from './page-shell'
import { MetricView } from './components/MetricView'
import { RankedListView } from './components/RankedListView'
import { ReportChart } from './components/ReportChart'
import { SourceBadge } from './components/LibraryTable'
import { ReportTable } from './components/ReportTable'
import { QuickFilterBar, QuickState } from './components/QuickFilterBar'
import { TableCard } from './components/TableCard'
import {
  availableViews,
  defaultView,
  ReportView,
  ViewSwitcher,
} from './components/ViewSwitcher'
import { csvFilename, downloadCsv, toCsv } from './csv'
import { deleteSavedView, getSavedView, saveView } from './saved-views'
import { SaveDialog, SaveResult } from './components/SaveDialog'
import { getTemplate } from './templates'
import { useReportResult } from './use-report-result'
import { ViewConfig } from './view-config'

/** Shared row styling for the "More actions" (⋯) overflow menu items. */
const overflowItemClass =
  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-surface-hover'

/** Amber status dot overlaid top-right of a toggle icon to flag an active state. */
function ActiveDot() {
  return (
    <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-warning ring-2 ring-surface" />
  )
}

export default function ReportDetailPage() {
  const { viewId = '' } = useParams()
  const initial = getTemplate(viewId) ?? getSavedView(viewId)

  return <ViewConfigReportDetail viewId={viewId} initial={initial} />
}

/** Detail surface for a ViewConfig-based report (template or saved copy). */
function ViewConfigReportDetail({
  viewId,
  initial,
}: {
  viewId: string
  initial: ViewConfig | undefined
}) {
  const navigate = useNavigate()

  // key={viewId} on the route element remounts this component per view,
  // so initializing state from props here is safe (see desktop.tsx).
  const [config] = useState<ViewConfig | undefined>(initial)
  const [view, setView] = useState<ReportView>(() =>
    initial ? defaultView(initial) : 'table'
  )
  const [page, setPage] = useState(0)
  const handleSetView = (v: ReportView) => {
    setView(v)
    setPage(0)
  }
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const [saveQuickOpen, setSaveQuickOpen] = useState(false)
  const [newDashOpen, setNewDashOpen] = useState(false)
  // Notion-style: the conditions row is hidden until the header Filter/Sort
  // toggles reveal it. Active filters stay applied while collapsed (the toggle
  // dots flag that), so this controls visibility only — never the query.
  const [showFilters, setShowFilters] = useState(false)
  // Session-layer quick filters/sort the viewer lays over the saved report.
  // Not auto-saved — "Save as report" snapshots the merged config as a new
  // report. Resets per report since the route remounts on viewId change.
  const [quick, setQuick] = useState<QuickState>({ filters: [] })
  const effectiveConfig = useMemo(
    () =>
      config
        ? {
            ...config,
            filters: [...config.filters, ...quick.filters],
            ...(quick.sort ? { sort: quick.sort } : {}),
          }
        : undefined,
    [config, quick]
  )
  const { result } = useReportResult(effectiveConfig)
  const { kpis } = useKpis()

  if (!config || !result || !effectiveConfig) {
    return (
      <div className="p-8">
        <p className="text-fg-secondary">Report not found.</p>
        <Link to="/reporting" className="text-brand">
          Back to reports
        </Link>
      </div>
    )
  }

  // Saving a *saved* report offers overwrite-in-place; a shipped template can
  // only be saved as a new copy (its definition stays put).
  const isSaved = !!getSavedView(viewId)
  const handleSaveNew = ({ name, description }: SaveResult) => {
    const saved = saveView({ ...config, description }, name)
    navigate(`/reporting/${saved.id}`)
  }
  const handleSaveQuick = ({ name, description }: SaveResult) => {
    const saved = saveView({ ...effectiveConfig, description }, name)
    navigate(`/reporting/${saved.id}`)
  }
  const handleExport = () =>
    downloadCsv(csvFilename(config.name), toCsv(effectiveConfig, result))

  // Dashboards this report can be added to: selecting one appends the report as
  // a tile and opens that dashboard so the new tile is visible.
  const dashboards = listSavedDashboards()

  // Only user-saved reports can be deleted (templates are shipped).
  const handleDelete = () => {
    if (!window.confirm(`Delete "${config.name}"? This can't be undone.`))
      return
    deleteSavedView(viewId)
    navigate('/reporting')
  }

  const displayConfig = resolveThreshold(config, kpis)

  const views = availableViews(config, result)
  const showQuickBar = config.kind === 'list' && view === 'table'
  const PAGE_SIZE = 25
  const totalRows =
    result.kind === 'list' ? result.rows.length : result.groups.length
  const totalPages = Math.ceil(totalRows / PAGE_SIZE)
  // Clamp: a quick filter/sort can shrink the result under the current page
  // (e.g. page 3 → 1 page of results). Derive the page instead of resetting it
  // so the pager never renders an out-of-range empty slice.
  const safePage = Math.min(page, Math.max(0, totalPages - 1))
  const paginatedResult =
    view === 'table'
      ? result.kind === 'list'
        ? {
            ...result,
            rows: result.rows.slice(
              safePage * PAGE_SIZE,
              (safePage + 1) * PAGE_SIZE
            ),
          }
        : {
            ...result,
            groups: result.groups.slice(
              safePage * PAGE_SIZE,
              (safePage + 1) * PAGE_SIZE
            ),
          }
      : result

  const viewToolbar =
    views.length > 1 ? (
      <div className="ml-auto">
        <ViewSwitcher
          config={config}
          result={result}
          view={view}
          onChange={handleSetView}
        />
      </div>
    ) : undefined

  return (
    <div className={cn(REPORTING_PAGE_SHELL, 'gap-4')}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Link
              to="/reporting"
              className="inline-flex items-center gap-1 text-sm text-fg-secondary hover:text-fg"
            >
              <PiCaretLeft size={14} /> Reports
            </Link>
            <SourceBadge source={isSaved ? 'mine' : 'prebuilt'} />
          </div>
          <h1 className="text-2xl font-bold text-fg">{config.name}</h1>
          {config.description && (
            <p className="text-sm text-fg-secondary">{config.description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {showQuickBar && (
            <>
              <button
                type="button"
                aria-label="Filter"
                aria-pressed={showFilters}
                onClick={() => setShowFilters((v) => !v)}
                className={cn(
                  'relative inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                  showFilters
                    ? 'border-brand-active text-brand-active'
                    : 'border-line text-fg-secondary hover:text-fg'
                )}
              >
                <PiFunnel size={18} />
                {quick.filters.length > 0 && <ActiveDot />}
              </button>
              <button
                type="button"
                aria-label="Sort"
                aria-pressed={showFilters}
                onClick={() => setShowFilters((v) => !v)}
                className={cn(
                  'relative inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                  showFilters
                    ? 'border-brand-active text-brand-active'
                    : 'border-line text-fg-secondary hover:text-fg'
                )}
              >
                <PiArrowsDownUp size={18} />
                {quick.sort && <ActiveDot />}
              </button>
            </>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" className={secondaryPillButtonClass}>
                <PiPlus size={16} /> Add to…
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-1">
              <p className="px-2 pb-0.5 pt-1.5 text-xxs font-semibold uppercase tracking-wide text-fg-secondary">
                Add to dashboard
              </p>
              {dashboards.length > 0 ? (
                dashboards.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => {
                      addReportToDashboard(d.id, config)
                      navigate(`/reporting/dashboards/${d.id}`)
                    }}
                    className="flex w-full rounded px-2 py-1.5 text-left text-sm text-fg hover:bg-surface-hover"
                  >
                    {d.name}
                  </button>
                ))
              ) : (
                <p className="px-2 py-1.5 text-sm text-fg-secondary">
                  No dashboards yet
                </p>
              )}
              <button
                type="button"
                onClick={() => setNewDashOpen(true)}
                className="mt-1 flex w-full items-center gap-1.5 rounded border-t border-line px-2 py-2 text-left text-sm text-brand-active hover:bg-surface-hover"
              >
                <PiPlus size={14} /> New dashboard…
              </button>
            </PopoverContent>
          </Popover>
          {isSaved && (
            <button
              type="button"
              onClick={() => navigate(`/reporting/${viewId}/edit`)}
              className={secondaryPillButtonClass}
            >
              <PiPencilSimple size={16} /> Edit
            </button>
          )}
          <button
            type="button"
            onClick={handleExport}
            className={secondaryPillButtonClass}
          >
            <PiDownloadSimple size={16} /> Export CSV
          </button>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                className={cn(secondaryPillButtonClass, 'px-2')}
              >
                <PiDotsThree size={18} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-44 p-1">
              <button
                type="button"
                onClick={() => setDuplicateOpen(true)}
                className={cn(overflowItemClass, 'text-fg')}
              >
                <PiCopy size={16} /> Duplicate
              </button>
              {isSaved && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className={cn(overflowItemClass, 'text-error')}
                >
                  <PiTrash size={16} /> Delete
                </button>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {showQuickBar && showFilters && (
        <QuickFilterBar
          config={config}
          quick={quick}
          onChange={setQuick}
          onSaveAsReport={() => setSaveQuickOpen(true)}
        />
      )}

      {view === 'metric' ? (
        <div className="flex flex-col gap-4">
          {viewToolbar && (
            <div className="flex flex-wrap items-end gap-4">{viewToolbar}</div>
          )}
          <div className="w-full max-w-sm rounded-2xl border border-line bg-surface p-5">
            <MetricView config={displayConfig} result={result} />
          </div>
        </div>
      ) : (
        <TableCard toolbar={viewToolbar}>
          {view === 'table' ? (
            <>
              <div className="flex h-[calc(100vh-26rem)] flex-col">
                <ReportTable
                  config={effectiveConfig}
                  result={paginatedResult}
                  summaryResult={result}
                  fill
                />
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-line px-4 py-2 text-xs text-fg-secondary">
                  <span>{totalRows.toLocaleString()} rows total</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={safePage === 0}
                      onClick={() => setPage(safePage - 1)}
                      className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-surface-hover disabled:opacity-40"
                    >
                      <PiCaretLeft size={12} /> Prev
                    </button>
                    <span>
                      Page {safePage + 1} of {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={safePage >= totalPages - 1}
                      onClick={() => setPage(safePage + 1)}
                      className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-surface-hover disabled:opacity-40"
                    >
                      Next <PiCaretRight size={12} />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : view === 'ranked' ? (
            <div className="h-[calc(100vh-26rem)] overflow-auto p-4">
              <RankedListView config={config} result={result} />
            </div>
          ) : (
            <div className="h-[calc(100vh-26rem)] flex flex-col p-4">
              <div className="min-h-0 flex-1">
                <ReportChart
                  bare
                  fill
                  chartType={view}
                  config={displayConfig}
                  result={result}
                />
              </div>
            </div>
          )}
        </TableCard>
      )}

      <SaveDialog
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        kind="report"
        titleOverride="Duplicate report"
        acceptTextOverride="Duplicate"
        defaultName={`${config.name} (copy)`}
        defaultDescription={config.description}
        onSaveNew={handleSaveNew}
      />

      <SaveDialog
        open={saveQuickOpen}
        onClose={() => setSaveQuickOpen(false)}
        kind="report"
        titleOverride="Save as new report"
        defaultName={`${config.name} (filtered)`}
        defaultDescription={config.description}
        onSaveNew={handleSaveQuick}
      />

      <SaveDialog
        open={newDashOpen}
        onClose={() => setNewDashOpen(false)}
        kind="dashboard"
        titleOverride="New dashboard"
        defaultName={config.name}
        onSaveNew={({ name, description }) => {
          const created = createDashboardFromReport(
            name,
            config,
            description || undefined
          )
          navigate(`/reporting/dashboards/${created.id}`)
        }}
      />
    </div>
  )
}
